import { Results as ReactifyResults, useResults } from "@usereactify/search";
import React, { useEffect, useRef, useState } from "react";
import { ResultCardProduct } from "@app/scripts/reactify/results/ResultCardProduct";
import { ResultCardCallout } from "@app/scripts/reactify/results/ResultCardCallout";
import { ResultPaginationLoadMore } from "@app/scripts/reactify/pagination/LoadMore";
import { ResultPaginationNumbered } from "@app/scripts/reactify/pagination/Numbered";
import { CollectionToolBar } from "../CollectionToolBar/CollectionToolBar";
import { isMobile } from '../../react-hooks/Utilties';


export const Results: React.FC<{ ReactifySearchData?: any, searchTerm?: string }> = ({ ReactifySearchData, searchTerm = "" }) => {
    const { collection } = ReactifySearchData
    const { resultStats, loading } = useResults();
    const containerRef = useRef(null);
    const collectionData = window.subCollections;
    const [productsPerRow, setProductsPerRow] = useState<string>(isMobile ? "two" : "four");
    const [resultRendered, setResultRendered] = useState(false);
    const [collectionHandle, setCollectionHandle] = useState(collection?.handle || "");
    // useResults().loading flips to false as soon as resultStats.numberOfResults is defined, and a
    // zero result response defines it. Driving the loaded state from a child card mount instead meant
    // an empty result set never dismissed the skeleton.
    const reactifyLoaded = !loading

    const resultStatsRef = useRef(resultStats);
    useEffect(() => {
        resultStatsRef.current = resultStats;
    }, [resultStats]);


    const isBrowser = typeof window !== "undefined"
    const storedPageData = isBrowser && localStorage.getItem('collectionScrollPosition');

    const getInitialPageSize = () => {
        return storedPageData && collectionHandle === JSON.parse(storedPageData)?.collectionHandle ? JSON.parse(storedPageData)?.size : 24
    };

    const [pageSize, setPageSize] = useState(getInitialPageSize);

    useEffect(() => {
        if (resultRendered) {
            document.dispatchEvent(new CustomEvent("swym:collections-loaded"));
        }
    }, [resultRendered]);

    const handleResultRender = () => {
        setResultRendered(true);
    };

    const handlePageSizeStorage = () => {
        if (isBrowser)
            localStorage.setItem(
                "collectionScrollPosition",
                JSON.stringify({
                    size: resultStats?.displayedResults || 28,
                    position: document.documentElement.scrollTop,
                    collectionHandle
                })
            )
    }

    useEffect(() => {
        // Makes sure the user has used back navigation in the browser to stop this running in the case that they may have refreshed
        const isBackNavigation = window.performance?.getEntriesByType?.("navigation")?.[0]?.type === "back_forward";

        if (reactifyLoaded && isBackNavigation) {
            if (collectionHandle === JSON.parse(storedPageData)?.collectionHandle) {
                setTimeout(() => {
                    window.scrollTo({ top: JSON.parse(storedPageData)?.position || 0 });
                }, 1000);
            }
        }
    }, [reactifyLoaded, collectionHandle, storedPageData]);



    useEffect(() => {
        if (reactifyLoaded) {
            // results-skeleton.ts owns the skeleton. resultStats is current whenever reactifyLoaded
            // flips, so it is deliberately not a dependency here: this fires once per page load.
            document.dispatchEvent(
                new CustomEvent("results:rendered", { detail: { count: resultStats?.numberOfResults ?? 0 } })
            )
        }
    }, [reactifyLoaded])


    return (
        <div ref={containerRef} className="main-collection-product-grid">
            <div className={`${!reactifyLoaded ? "hidden" : "w-full sm:hidden"}`}>
                <p className="collection-tool-bar__title">{collection?.title || searchTerm}</p>
                <p className={`collection-tool-bar__search-results ${!searchTerm && "hidden"}`}>{resultStats?.numberOfResults} results for "{searchTerm}"</p>
                <div className="collection-tool-bar__menu flex gap-3 align-end">
                    {collectionData &&
                        collectionData.map((collection: any) => (
                            <a href={`${collection.url}`} className="collection-tool-bar__menu-item" key={collection.url}>
                                {collection.title}
                            </a>
                        ))
                    }
                </div>
            </div>
            <CollectionToolBar setProductsPerRow={setProductsPerRow} collection={collection} reactifyLoaded={reactifyLoaded} searchTerm={searchTerm} results={resultStats?.numberOfResults} />
            <ReactifyResults
                pageSize={pageSize}
                listStyle={{}}
                listClassName={`main-collection-product-grid__products ${productsPerRow}-col ${!reactifyLoaded ? "hidden" : "grid"}`}
                renderResultCardProduct={(props) => (
                    <ResultCardProduct {...props} onRender={handleResultRender} handlePageSizeStorage={handlePageSizeStorage} />
                )}
                renderResultCardCallout={(props) => <ResultCardCallout productsPerRow={productsPerRow} {...props} />}
                renderPaginationLoadMore={(props) => <ResultPaginationLoadMore {...props} />}
                renderPaginationNumbered={(props) => <ResultPaginationNumbered {...props} />}
                renderNoResults={NoResults}
                renderLoading={() => (
                    <div className="rs_loading"></div>
                )}
            />
        </div>
    );
};

const NoResults = () => {
    useEffect(() => {
        document.body.classList.add("no-search-results");
        return () => {
            document.body.classList.remove("no-search-results");
        };
    }, []);
    return null;
};
